// src/components/resources/tools/generators/RedditPost/scoring.ts

/**
 * Pure score/format helpers for the Reddit post mockup, split out from the
 * `'use client'` component so they can be unit-tested in the node test env
 * without rendering React.
 */

/**
 * Vote state for the interactive up/down arrows. `null` = no active vote
 * (the displayed score is the configured base score).
 */
export type VoteState = 'up' | 'down' | null

/**
 * Apply the interactive vote offset to the configured base score. Neutral
 * (`null`) returns the score unchanged, so the displayed/exported count equals
 * the configured Upvotes value until the user actually votes.
 */
export function applyVote(score: number, vote: VoteState): number {
  if (vote === 'up') return score + 1
  if (vote === 'down') return score - 1
  return score
}

/**
 * Format a count for display (e.g., 1280 -> "1.3k", 2_400_000 -> "2.4m").
 * Reddit lowercases the unit suffix on vote/comment counters and drops a
 * trailing ".0" (2000 -> "2k", not "2.0k"). The lowercase suffix is a
 * deliberate Reddit-parity choice — the social comment layouts use the
 * uppercase-suffix `formatCompactCount` in `CommentThread/threadModel.ts`
 * to match Instagram/YouTube/TikTok/X conventions instead.
 *
 * Negative values (comment-thread scores can go below zero) compact with the
 * sign preserved: -2500 -> "-2.5k", matching the positive path's units.
 *
 * Rounding is checked AFTER `toFixed` so a value that rounds up across a unit
 * boundary is promoted to the larger unit — e.g. 999_950 formats as "1m", not
 * "1000k" (and 999_950_000 as "1b", not "1000m").
 */
export function formatCount(value: number): string {
  if (value < 0) return `-${formatCount(-value)}`
  const unit = (n: number, divisor: number, suffix: string): string =>
    `${(n / divisor).toFixed(1).replace(/\.0$/, '')}${suffix}`
  if (value >= 1_000_000_000) return unit(value, 1_000_000_000, 'b')
  if (value >= 1_000_000) {
    return Number((value / 1_000_000).toFixed(1)) >= 1000 ? unit(value, 1_000_000_000, 'b') : unit(value, 1_000_000, 'm')
  }
  if (value >= 1_000) {
    return Number((value / 1_000).toFixed(1)) >= 1000 ? unit(value, 1_000_000, 'm') : unit(value, 1_000, 'k')
  }
  return value.toString()
}
